<template>
  <div
    class="modal-body call-panel-body"
    style="flex: 1; overflow: hidden; display: flex; flex-direction: column"
  >
    <div class="sms-layout">
      <!-- Loading State -->
      <template v-if="isChannelsLoading || isLoadingPhoneNumbers">
        <div class="sms-loading-state">
          <LoadingBars size="sm" />
        </div>
      </template>

      <!-- No Org Phone Number State -->
      <template v-else-if="!hasOrgPhoneNumbers">
        <div class="sms-empty-state sms-empty-state--no-phone">
          <div class="sms-empty-state__content">
            <Icon
              icon="mdi:message-off-outline"
              class="sms-empty-state__icon sms-empty-state__icon--sm"
            />
            <h3 class="sms-empty-state__title">No SMS Number</h3>
            <p class="sms-empty-state__message">
              Purchase a Twilio phone number to start sending SMS messages.
            </p>
            <Button variant="primary" size="sm" icon="mdi:plus" @click="navigateToPurchaseNumber">
              Purchase Number
            </Button>
          </div>
        </div>
      </template>

      <!-- No Contact Phone Number State -->
      <template v-else-if="!hasValidPhoneNumber">
        <div class="sms-empty-state sms-empty-state--no-phone">
          <div class="sms-empty-state__content">
            <Icon
              icon="mdi:phone-off-outline"
              class="sms-empty-state__icon sms-empty-state__icon--sm"
            />
            <h3 class="sms-empty-state__title">No Contact Phone</h3>
            <p class="sms-empty-state__message">
              Add a phone number to this contact to send SMS messages.
            </p>
            <Button
              variant="primary"
              size="sm"
              icon="mdi:pencil-outline"
              @click="$emit('show-edit-contact-modal')"
            >
              Edit Contact
            </Button>
          </div>
        </div>
      </template>

      <!-- CONVERSATION VIEWS -->
      <div v-else class="sms-content-wrapper">
        <OutreachCueCards
          :icp-options="smsICPOptions"
          :icp-loading="props.icpLoading"
          :contact-id="selectedBucket?.contact?.id || ''"
          :contact-name="
            [selectedBucket?.contact?.fname, selectedBucket?.contact?.lname]
              .filter(Boolean)
              .join(' ')
          "
          :full-width="true"
          @use-content="handleUseCueCardContent"
        />

        <!-- CONVERSATION LIST VIEW -->
        <div v-if="!showDetailView" class="sms-list-view">
          <div class="sms-sidebar-header">
            <Button
              variant="primary"
              size="sm"
              icon="mdi:message-plus"
              @click="showStartChatModal = true"
              block
            >
              Start New Chat
            </Button>
          </div>
          <div
            v-if="isLoadingConversations || (isPendingConversations && !!contactIdRef)"
            class="sms-conversations-loading"
          >
            <LoadingBars size="sm" />
          </div>

          <div
            v-else-if="isConversationsError"
            class="sms-empty-state sms-empty-state--no-conversations"
          >
            <div class="sms-empty-state__content">
              <Icon icon="mdi:alert-circle-outline" class="sms-empty-state__icon" />
              <h3 class="sms-empty-state__title">Failed to load conversations</h3>
              <p class="sms-empty-state__message">Something went wrong. Please try again.</p>
              <Button variant="outline" size="sm" @click="refetchConversations()"> Retry </Button>
            </div>
          </div>

          <div
            v-else-if="formattedConversations.length === 0"
            class="sms-empty-state sms-empty-state--no-conversations"
          >
            <div class="sms-empty-state__content">
              <Icon icon="mdi:message-text-outline" class="sms-empty-state__icon" />
              <h3 class="sms-empty-state__title">No Conversations</h3>
              <p class="sms-empty-state__message">
                Start a new chat to begin messaging this contact.
              </p>
            </div>
          </div>

          <ConversationList
            v-else
            :conversations="formattedConversations as any"
            :selected-conversation-id="selectedConversationId ?? undefined"
            :is-loading="isLoadingConversations"
            type="sms"
            :show-search="false"
            :show-header="false"
            @conversation-selected="handleConversationSelected"
          />
        </div>

        <!-- MESSAGE DETAIL VIEW -->
        <div v-else class="sms-main-content">
          <div class="sms-main-header">
            <button class="sms-main-header__back-btn" @click="handleBackToList">
              <Icon icon="mdi:chevron-left" />
            </button>
            <div class="sms-main-header__info">
              <span class="sms-main-header__name">{{
                selectedConversation?.contact
                  ? [selectedConversation.contact.fname, selectedConversation.contact.lname]
                      .filter(Boolean)
                      .join(" ") || "Unknown Contact"
                  : "Unknown Contact"
              }}</span>
              <span class="sms-main-header__separator">•</span>
              <div class="sms-main-header__phone-wrapper">
                <Icon icon="mdi:phone-outline" class="sms-main-header__phone-icon" />
                <span class="sms-main-header__phone">{{
                  selectedConversation?.contactPhoneNumber
                }}</span>
              </div>
            </div>
          </div>

          <!-- Messages Container -->
          <div class="sms-messages-container" ref="messagesContainer">
            <div v-if="isLoadingMessages" class="sms-state-message sms-loading-state">
              <LoadingBars size="sm" />
            </div>

            <div v-else-if="isMessagesError" class="sms-state-message sms-error-state">
              <Icon icon="mdi:alert-circle-outline" class="sms-state-icon" />
              <p class="sms-state-text">Failed to load messages</p>
              <button class="retry-btn" @click="refetchMessages()">Retry</button>
            </div>

            <div v-else-if="messages && messages.length > 0" class="messages-list">
              <div
                v-for="message in messages"
                :key="message.id"
                :class="['message-bubble', message.direction === 'outbound' ? 'sent' : 'received']"
              >
                <div class="message-content">
                  <p>{{ message.body }}</p>
                  <div class="message-meta">
                    <span class="timestamp">{{ formatTimestamp(message.sentAt) }}</span>
                    <Icon
                      v-if="message.direction === 'outbound'"
                      :icon="getStatusIcon(message.status)"
                      :class="['status-icon', `status-${message.status}`]"
                      :title="message.status"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="sms-state-message sms-empty-state">
              <Icon icon="mdi:message-text-outline" class="sms-state-icon" />
              <p class="sms-state-text">No messages yet. Start the conversation!</p>
            </div>
          </div>

          <!-- Message Composer -->
          <div class="sms-composer">
            <IcpPicker
              :show="showIcpPicker"
              :mode="icpPickerMode"
              :icps="icps"
              class="sms-composer__icp-picker-positioned"
              @select="selectIcp"
              @dismiss="dismissIcpPicker"
            />

            <div class="sms-composer__ai-buttons-wrapper">
              <button
                v-tooltip="{
                  content: USAGE_BLOCKED_TOOLTIP,
                  disabled: !isUsageBlocked || isGeneratingAI,
                }"
                ref="aiComposeButtonRef"
                type="button"
                class="sms-composer__ai-btn sms-composer__ai-btn--compose"
                :disabled="isGeneratingAI || isUsageBlocked"
                :title="isUsageBlocked ? '' : isGeneratingAI ? 'Generating...' : 'AI Compose'"
                @click.stop="handleSMSAiCompose"
              >
                <template v-if="isGeneratingAI && generatingAction === 'compose'">
                  <span class="sms-composer__ai-spinner" />
                  <span class="sms-composer__ai-btn-text">Generating...</span>
                </template>
                <template v-else>
                  <Icon icon="mdi:sparkles" />
                  <span class="sms-composer__ai-btn-text">AI Compose</span>
                </template>
              </button>
              <button
                v-tooltip="{
                  content: USAGE_BLOCKED_TOOLTIP,
                  disabled: !isUsageBlocked || isGeneratingAI,
                }"
                type="button"
                class="sms-composer__ai-btn sms-composer__ai-btn--reply"
                :disabled="isGeneratingAI || isUsageBlocked"
                :title="isUsageBlocked ? '' : isGeneratingAI ? 'Generating...' : 'AI Reply'"
                @click.stop="handleSMSAiReply"
              >
                <template v-if="isGeneratingAI && generatingAction === 'reply'">
                  <span class="sms-composer__ai-spinner" />
                  <span class="sms-composer__ai-btn-text">Generating...</span>
                </template>
                <template v-else>
                  <Icon icon="mdi:sparkles" />
                  <span class="sms-composer__ai-btn-text">AI Reply</span>
                </template>
              </button>
              <button
                v-tooltip="{
                  content: USAGE_BLOCKED_TOOLTIP,
                  disabled: !isUsageBlocked || isGeneratingAI,
                }"
                type="button"
                class="sms-composer__ai-btn sms-composer__ai-btn--followup"
                :disabled="isGeneratingAI || isUsageBlocked"
                :title="isUsageBlocked ? '' : isGeneratingAI ? 'Generating...' : 'AI Follow-up'"
                @click.stop="handleSMSAiFollowup"
              >
                <template v-if="isGeneratingAI && generatingAction === 'followup'">
                  <span class="sms-composer__ai-spinner" />
                  <span class="sms-composer__ai-btn-text">Generating...</span>
                </template>
                <template v-else>
                  <Icon icon="mdi:sparkles" />
                  <span class="sms-composer__ai-btn-text">AI Follow-up</span>
                </template>
              </button>
              <button
                v-tooltip="{
                  content: USAGE_BLOCKED_TOOLTIP,
                  disabled: !isUsageBlocked || isGeneratingAI || isGeneratingAiCustom,
                }"
                ref="aiRefineButtonRef"
                type="button"
                class="sms-composer__ai-btn sms-composer__ai-btn--custom"
                :disabled="isGeneratingAI || isGeneratingAiCustom || isUsageBlocked"
                :title="isUsageBlocked ? '' : isGeneratingAiCustom ? 'Generating...' : 'AI Refine'"
                @click.stop="toggleAiRefinePanel"
              >
                <Icon icon="mdi:sparkles" />
                <span class="sms-composer__ai-btn-text">AI Refine</span>
              </button>
            </div>

            <div class="sms-composer__editor-wrapper">
              <textarea
                ref="textareaRef"
                v-model="messageBody"
                @input="updateCharCount"
                @keydown.enter.ctrl="handleSend"
                placeholder="Type your message..."
                :maxlength="1600"
                rows="2"
                class="sms-composer__textarea"
              ></textarea>
            </div>

            <div
              v-if="showEmojiPopover"
              class="sms-composer__emoji-container"
              @click.self="closeEmojiPopover"
            >
              <EmojiPicker @select="handleEmojiSelect" />
            </div>
            <AiRefinePopover
              :show="showAiPanel"
              :trigger-el="aiRefineButtonRef"
              :disabled="isGeneratingAiCustom"
              @generate="handleAiCustomizationGenerate"
              @close="showAiPanel = false"
            />

            <AiRefinePopover
              :show="showAiComposePanel"
              :trigger-el="aiComposeButtonRef"
              :disabled="isGeneratingAI && generatingAction === 'compose'"
              title="AI Compose"
              generate-button-label="Generate Message"
              :show-attachments="false"
              @generate="handleAiComposeGenerate"
              @close="showAiComposePanel = false"
            />

            <div class="sms-composer__footer">
              <div class="sms-composer__footer-left">
                <div class="sms-composer__char-counter">
                  <span>{{ charCount }}/{{ maxChars }}</span>
                </div>
              </div>
              <div class="sms-composer__footer-right" ref="sendButtonRef">
                <div class="sms-composer__footer-inline-tools">
                  <div class="sms-composer__popover-wrapper" ref="linkPopoverWrapperRef">
                    <button
                      type="button"
                      class="sms-composer__footer-icon-btn"
                      title="Insert link"
                      @click.stop="toggleLinkPopover"
                    >
                      <Icon icon="mdi:link" />
                    </button>
                    <div
                      v-if="showLinkPopover"
                      ref="linkPopoverRef"
                      class="sms-composer__popover sms-composer__popover--link"
                      :style="popoverStyle"
                      @click.stop
                    >
                      <div class="sms-composer__popover-content">
                        <div class="sms-composer__link-field-wrapper">
                          <Icon
                            icon="mdi:format-align-left"
                            class="sms-composer__link-field-icon"
                          />
                          <input
                            v-model="linkText"
                            type="text"
                            class="sms-composer__link-input sms-composer__link-input--text"
                            placeholder="Text"
                            @keyup.enter="insertLink"
                            @keyup.esc="closeLinkPopover"
                          />
                        </div>
                        <div
                          class="sms-composer__link-field-wrapper sms-composer__link-field-wrapper--url"
                        >
                          <Icon icon="mdi:link" class="sms-composer__link-field-icon" />
                          <input
                            v-model="linkUrl"
                            type="text"
                            class="sms-composer__link-input sms-composer__link-input--url"
                            placeholder="Type or paste a link"
                            @keyup.enter="insertLink"
                            @keyup.esc="closeLinkPopover"
                            ref="linkInputRef"
                          />
                          <button
                            type="button"
                            class="sms-composer__link-apply-btn"
                            @click="insertLink"
                            :disabled="!linkUrl || linkUrl.trim() === ''"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="sms-composer__footer-icon-btn"
                    title="Insert emoji"
                    @click.stop="toggleEmojiPopover"
                  >
                    <Icon icon="mdi:emoticon-outline" />
                  </button>
                </div>
                <button
                  v-tooltip="{
                    content: USAGE_BLOCKED_TOOLTIP,
                    disabled: !isUsageBlocked || !canSend,
                  }"
                  class="sms-composer__send-btn"
                  @click="handleSend"
                  :disabled="!canSend || isUsageBlocked"
                  :class="{ 'sms-composer__send-btn--sending': isSending }"
                >
                  <Icon v-if="isSending" icon="mdi:loading" class="sms-composer__spin" />
                  <Icon v-else icon="mdi:send" />
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Start Chat Modal -->
        <StartSMSChatModal
          :show="showStartChatModal"
          :org-phone-numbers="orgPhoneNumbers"
          :contact-phone-numbers="contactPhoneNumbers"
          :contact-id="selectedBucket?.contact?.id || ''"
          :is-loading="isCreatingConversation"
          :initial-message="initialModalMessage"
          :icp-id="icps?.find((i) => i.isDefault)?.id"
          @close="
            showStartChatModal = false;
            initialModalMessage = '';
          "
          @create-chat="handleCreateChat"
        />

        <ActionConfirmationModal
          :show="showAiActionConfirm"
          :title="aiActionConfirmTitle"
          :message="aiActionConfirmMessage"
          confirm-text="Generate"
          cancel-text="Cancel"
          confirm-variant="primary"
          dialog-max-width="340px"
          @confirm="confirmAiAction"
          @close="cancelAiAction"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { Icon } from "@iconify/vue";
import { useRouter } from "vue-router";
import { Button } from "@/components/ui";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import ConversationList from "@/components/bucket/interactions/ConversationList.vue";
import OutreachCueCards from "@/components/bucket/ai/OutreachCueCards.vue";
import EmojiPicker from "@/components/socials/EmojiPicker.vue";
import AiRefinePopover from "@/components/socials/AiRefinePopover.vue";
import { useResolvedAiPrompts } from "@/composables/useAiPrompts";
import StartSMSChatModal from "@/components/sms/StartSMSChatModal.vue";
import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";
import { useSendSMS } from "@/composables/sms/useSendSMS";
import {
  useContactSMSConversations,
  useSMSConversationMessages,
  useCreateSMSConversation,
  type SMSConversation,
} from "@/composables/sms";
import { useSMSSocketListeners } from "@/composables/sms/useSMSSocketListeners";
import { useBillingStatus, USAGE_BLOCKED_TOOLTIP } from "@/composables/billing/useBillingStatus";
import { GET_PHONE_NUMBERS } from "@/store/action-types";
import { useQueryClient } from "@tanstack/vue-query";
import { useContactData } from "@/composables/contacts/useContactData";
import { useStore } from "vuex";
import { getFriendlyTwilioErrorMessage } from "@/utils/twilioErrors";
import AIGenerationService from "@/services/ai/ai-generation";
import IcpPicker from "@/components/ui/IcpPicker.vue";
import { useIcpPicker } from "@/composables/ui/useIcpPicker";
import { useMessageDraft } from "@/composables/messages/useMessageDraft";
import { normalizeSMSMessages } from "@/utils/conversationNormalizer";
import { SETTINGS_CALL_PHONE_NUMBERS_ROUTE } from "@/router/routes";

interface Props {
  selectedBucket: any;
  contact?: any;
  isActive: boolean;
  isChannelsLoading?: boolean;
  icps?: Array<{ id: string; name: string; isDefault?: boolean }>;
  icpLoading?: boolean;
  pendingCueCardContent?: any;
}

const props = withDefaults(defineProps<Props>(), {
  isChannelsLoading: false,
  icps: () => [],
  icpLoading: false,
  pendingCueCardContent: null,
});

const emit = defineEmits<{
  (e: "refresh-conversations"): void;
  (e: "message-sent"): void;
  (e: "show-edit-contact-modal"): void;
  (
    e: "open-ai-personalization",
    payload: { defaultType: string; contactName?: string; icpName?: string }
  ): void;
  (e: "switch-channel", payload: any): void;
  (e: "content-applied"): void;
}>();

const $toast = useToast();
const store = useStore();
const router = useRouter();

const { showUsageBasedFeaturesModalOnce, isUsageBlocked } = useBillingStatus();

const queryClient = useQueryClient();
const contactIdRef = ref<string | null>(null);

// Get organization ID from store
const organizationId = computed(() => store.getters.user?.organizationId || "");

// Get phone numbers from Vuex store
const storePhoneNumbers = computed(() => store.getters.phoneNumbers);
const isLoadingPhoneNumbers = computed(() => storePhoneNumbers.value?.isLoading || false);

// Fetch phone numbers if not already loaded
const fetchPhoneNumbers = () => {
  if (organizationId.value && !storePhoneNumbers.value?.data?.length) {
    store.dispatch(GET_PHONE_NUMBERS, { organizationId: organizationId.value });
  }
};
const selectedConversationId = ref<string | null>(null);

// View state
const showDetailView = ref(false);
const selectedConversation = ref<SMSConversation | null>(null);
const showStartChatModal = ref(false);
const initialModalMessage = ref("");

const {
  data: conversationsData,
  isLoading: isLoadingConversations,
  isPending: isPendingConversations,
  isError: isConversationsError,
  refetch: refetchConversations,
} = useContactSMSConversations(contactIdRef);

const {
  data: messagesData,
  isLoading: isLoadingMessages,
  isError: isMessagesError,
  refetch: refetchMessages,
} = useSMSConversationMessages(selectedConversationId);

const { mutate: sendSMS, isPending: isSending } = useSendSMS();
const { mutate: createConversation, isPending: isCreatingConversation } =
  useCreateSMSConversation();

const { fetchDraft, scheduleSave, flushSave, clearDraft } = useMessageDraft("sms");
let currentDraftConversationId = "";

// Message composer state
const messagesContainer = ref<HTMLElement | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);
const messageBody = ref("");
const charCount = ref(0);
const maxChars = 1600;
const isGeneratingAI = ref(false);
const generatingAction = ref<string | null>(null);
const showAiPanel = ref(false);
const showAiComposePanel = ref(false);
const isGeneratingAiCustom = ref(false);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const aiComposeButtonRef = ref<HTMLElement | null>(null);
const pendingComposeIcpId = ref<string | undefined>(undefined);
const resolvedAiPromptsQuery = useResolvedAiPrompts();

// Emoji and link popover state
const showEmojiPopover = ref(false);
const showLinkPopover = ref(false);
const linkText = ref("");
const linkUrl = ref("");
const linkInputRef = ref<HTMLInputElement | null>(null);
const linkPopoverRef = ref<HTMLElement | null>(null);
const linkPopoverWrapperRef = ref<HTMLElement | null>(null);
const sendButtonRef = ref<HTMLElement | null>(null);
const popoverStyle = ref<{ bottom?: string; right?: string; left?: string }>({});

// Computed: Messages sorted for chat view (oldest first)
const messages = computed(() => {
  if (!messagesData.value?.messages) return [];
  // Messages come from API already sorted ASC (oldest first)
  return messagesData.value.messages;
});

// Computed: Format conversations for list
const formattedConversations = computed(() => {
  if (!conversationsData.value) return [];
  return (conversationsData.value as SMSConversation[]).map((conv) => {
    const contactName = conv.contact
      ? [conv.contact.fname, conv.contact.lname].filter(Boolean).join(" ") || "Unknown Contact"
      : "Unknown Contact";
    return {
      id: conv.id,
      title: contactName,
      subtitle: conv.contactPhoneNumber,
      lastMessage: conv.lastMessagePreview || "",
      lastMessageAt: conv.lastMessageAt || conv.createdAt,
      unreadCount: conv.unreadCount || 0,
      lastMessageDirection: conv.lastMessageDirection || null,
    };
  });
});

const smsICPOptions = computed(() => {
  const list = Array.isArray(props.icps) ? props.icps : [];
  return [
    { value: "", label: "Select ICP for SMS" },
    ...list.map((icp) => ({
      value: icp.id,
      label: icp.isDefault ? `${icp.name} (Default)` : icp.name,
    })),
  ];
});

const orgPhoneNumbers = computed(() => {
  const data = storePhoneNumbers.value?.data;
  if (!data || !Array.isArray(data)) return [];
  return data.map((phone: any) => ({
    id: phone.id,
    number: phone.number,
    region: phone.region,
    city: phone.city,
  }));
});

const hasOrgPhoneNumbers = computed(() => {
  return orgPhoneNumbers.value && orgPhoneNumbers.value.length > 0;
});

const contactPhoneNumbers = computed(() => {
  const { computeContactPhoneNumbers } = useContactData(store);
  const phones = computeContactPhoneNumbers({
    contact: props.contact ?? props.selectedBucket?.contact,
  });
  return phones.map((p: any) => ({
    id: p.id,
    label: p.type || "Phone",
    number: p.number,
  }));
});

const hasValidPhoneNumber = computed(() => {
  return contactPhoneNumbers.value && contactPhoneNumbers.value.length > 0;
});

const canSend = computed(() => {
  return (
    messageBody.value.trim().length > 0 &&
    !isSending.value &&
    charCount.value <= maxChars &&
    selectedConversation.value
  );
});

const updateCharCount = () => {
  charCount.value = messageBody.value.length;
};

watch(
  messageBody,
  () => {
    updateCharCount();
  },
  { immediate: true }
);

const loadSMSDraftForConversation = async (conversationId: string) => {
  currentDraftConversationId = conversationId;
  messageBody.value = "";
  if (!conversationId) return;
  const draftContent = await fetchDraft(conversationId);
  if (currentDraftConversationId === conversationId && draftContent) {
    messageBody.value = draftContent;
  }
};

watch(selectedConversationId, (newId, oldId) => {
  if (oldId) {
    flushSave(oldId, messageBody.value);
  }
  loadSMSDraftForConversation(newId || "");
});

watch(messageBody, (newVal) => {
  const conversationId = selectedConversationId.value;
  if (!conversationId) return;
  scheduleSave(conversationId, newVal);
});

const formatTimestamp = (timestamp: string | Date) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs < 0) return "Just now";

  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  const isCurrentYear = date.getFullYear() === now.getFullYear();
  if (isCurrentYear) {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "delivered":
    case "sent":
      return "mdi:check";
    case "failed":
    case "undelivered":
      return "mdi:alert-circle";
    case "queued":
    case "accepted":
    case "sending":
      return "mdi:clock-outline";
    default:
      return "mdi:check";
  }
};

const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

function applySMSContent(content: string) {
  messageBody.value = content;
  updateCharCount();
  $toast?.success("SMS message loaded from cue card");
}

function handleUseCueCardContent(payload: { channel: string; content: string; subject?: string }) {
  if (payload.channel === "sms" && payload.content) {
    if (showDetailView.value && selectedConversation.value) {
      applySMSContent(payload.content);
    } else {
      initialModalMessage.value = payload.content;
      showStartChatModal.value = true;
    }
  } else {
    emit("switch-channel", payload);
  }
}

// Handle conversation selection
const handleConversationSelected = (conversation: any) => {
  const fullConversation = conversationsData.value?.find((c) => c.id === conversation.id);
  if (fullConversation) {
    selectedConversationId.value = conversation.id;
    selectedConversation.value = fullConversation;
    showDetailView.value = true;

    if (fullConversation.unreadCount > 0) {
      const prevUnread = fullConversation.unreadCount;
      const contactId = props.selectedBucket?.contact?.id;

      queryClient.setQueryData(["sms-conversations", contactIdRef.value], (old: any) => {
        if (!old) return old;
        return old.map((c: any) => (c.id === conversation.id ? { ...c, unreadCount: 0 } : c));
      });

      if (contactId) {
        queryClient.setQueryData(["interactionCounts", contactId], (old: any) => {
          if (!old?.sms) return old;
          return {
            ...old,
            sms: {
              ...old.sms,
              unread: Math.max(0, (old.sms.unread || 0) - prevUnread),
            },
          };
        });
      }

      // Buckets table's Activity column is a separate bulk query, keyed by the page's
      // contactIds — the optimistic cache writes above don't touch it, so invalidate it too.
      queryClient.invalidateQueries({ queryKey: ["engagement-summary"] });
    }
  }
};

const handleBackToList = () => {
  selectedConversationId.value = null;
  selectedConversation.value = null;
  showDetailView.value = false;
  refetchConversations();
};

// Navigate to purchase phone number page
const navigateToPurchaseNumber = () => {
  router.push({ name: SETTINGS_CALL_PHONE_NUMBERS_ROUTE });
};

// Handle create chat
const handleCreateChat = async (request: {
  contactId: string;
  phoneNumberId: string;
  contactPhoneNumber: string;
  contactPhoneId?: string;
  message?: string;
}) => {
  const { message, ...createRequest } = request;

  createConversation(createRequest, {
    onSuccess: (newConversation) => {
      showStartChatModal.value = false;
      $toast?.success("Conversation created");

      // Navigate to the new conversation
      selectedConversationId.value = newConversation.id;
      selectedConversation.value = newConversation;
      showDetailView.value = true;

      // Refetch conversations list
      refetchConversations();

      // Send initial message if provided
      if (message) {
        showUsageBasedFeaturesModalOnce("sms", async () => {
          sendSMS(
            {
              to: request.contactPhoneNumber,
              body: message,
              contactId: request.contactId,
              conversationId: newConversation.id,
              phoneNumberId: request.phoneNumberId,
              contactPhoneId: request.contactPhoneId,
            },
            {
              onSuccess: async () => {
                refetchMessages();
                refetchConversations();
                await nextTick();
                scrollToBottom();
              },
              onError: (error: any) => {
                console.error("Failed to send initial SMS:", error);
                if (!error?.absorbedButRecoverable) {
                  const friendlyMessage = getFriendlyTwilioErrorMessage(error);
                  $toast?.error(friendlyMessage);
                }
              },
            }
          );
        });
      }
    },
    onError: (error: any) => {
      console.error("Failed to create conversation:", error);
      $toast?.error(error.response?.data?.message || "Failed to create conversation");
    },
  });
};

const showAiActionConfirm = ref(false);
const pendingAiActionMode = ref<"reply" | "followup" | null>(null);
const pendingAiActionIcpId = ref<string | undefined>(undefined);

const aiActionConfirmTitle = computed(() =>
  pendingAiActionMode.value === "followup" ? "Generate AI Follow-up" : "Generate AI Reply"
);
const aiActionConfirmMessage = computed(() =>
  pendingAiActionMode.value === "followup"
    ? "This will generate an AI-written follow-up message. Continue?"
    : "This will generate an AI-written reply. Continue?"
);

const confirmAiAction = () => {
  if (pendingAiActionMode.value === "reply") doSMSAiReply(pendingAiActionIcpId.value);
  else if (pendingAiActionMode.value === "followup") doSMSAiFollowup(pendingAiActionIcpId.value);
  showAiActionConfirm.value = false;
  pendingAiActionMode.value = null;
};

const cancelAiAction = () => {
  showAiActionConfirm.value = false;
  pendingAiActionMode.value = null;
};

const { showIcpPicker, icpPickerMode, triggerAiAction, selectIcp, dismissIcpPicker } = useIcpPicker(
  (mode, icpId) => {
    if (mode === "reply" || mode === "followup") {
      pendingAiActionMode.value = mode;
      pendingAiActionIcpId.value = icpId;
      showAiActionConfirm.value = true;
    } else {
      pendingComposeIcpId.value = icpId;
      showAiPanel.value = false;
      showAiComposePanel.value = true;
    }
  }
);

const toggleAiRefinePanel = () => {
  showAiComposePanel.value = false;
  showAiPanel.value = !showAiPanel.value;
};

const handleSMSAiReply = async () => {
  if (isGeneratingAI.value) return;
  triggerAiAction("reply", props.icps);
};

const doSMSAiReply = async (icpId?: string) => {
  if (isGeneratingAI.value) return;

  await showUsageBasedFeaturesModalOnce("ai_generation", async () => {
    isGeneratingAI.value = true;
    generatingAction.value = "reply";
    try {
      const conversationHistory = normalizeSMSMessages(messages.value || []);

      const contactId = props.selectedBucket?.contact?.id;

      const response = await AIGenerationService.generateReply({
        contactId,
        icpId,
        channel: "sms",
        conversationHistory,
      });

      messageBody.value = response.body.replace(/<br\s*\/?>/gi, "\n");
      updateCharCount();

      $toast?.success("AI reply generated");
    } catch (error) {
      $toast?.error("Failed to generate AI response");
    } finally {
      isGeneratingAI.value = false;
      generatingAction.value = null;
    }
  });
};

const handleSMSAiFollowup = async () => {
  if (isGeneratingAI.value) return;
  triggerAiAction("followup", props.icps);
};

const doSMSAiFollowup = async (icpId?: string) => {
  if (isGeneratingAI.value) return;

  await showUsageBasedFeaturesModalOnce("ai_generation", async () => {
    isGeneratingAI.value = true;
    generatingAction.value = "followup";
    try {
      const conversationHistory = normalizeSMSMessages(messages.value || []);

      const contactId = props.selectedBucket?.contact?.id;

      const response = await AIGenerationService.generateFollowup({
        contactId,
        icpId,
        channel: "sms",
        conversationHistory,
      });

      messageBody.value = response.body.replace(/<br\s*\/?>/gi, "\n");
      updateCharCount();

      $toast?.success("AI follow-up generated");
    } catch (error) {
      $toast?.error("Failed to generate AI response");
    } finally {
      isGeneratingAI.value = false;
      generatingAction.value = null;
    }
  });
};

const handleSMSAiCompose = async () => {
  if (isGeneratingAI.value) return;
  triggerAiAction("compose", props.icps);
};

const doSMSAiCompose = async (icpId?: string, customInstructions?: string) => {
  if (isGeneratingAI.value) return;

  await showUsageBasedFeaturesModalOnce("ai_generation", async () => {
    isGeneratingAI.value = true;
    generatingAction.value = "compose";
    try {
      const conversationHistory = normalizeSMSMessages(messages.value || []);

      const contactId = props.selectedBucket?.contact?.id;

      const response = await AIGenerationService.generateCompose({
        contactId,
        icpId,
        channel: "sms",
        conversationHistory,
        customInstructions,
        aiPromptCompose: resolvedAiPromptsQuery.composeAiPrompt.value,
      });

      messageBody.value = response.body.replace(/<br\s*\/?>/gi, "\n");
      updateCharCount();
      showAiComposePanel.value = false;

      $toast?.success("AI message composed");
    } catch (error) {
      $toast?.error("Failed to generate AI response");
    } finally {
      isGeneratingAI.value = false;
      generatingAction.value = null;
    }
  });
};

const handleAiComposeGenerate = (payload: { customInstructions: string }) => {
  doSMSAiCompose(pendingComposeIcpId.value, payload.customInstructions);
};

const handleAiCustomizationGenerate = async (payload: {
  customInstructions: string;
  attachments: File[];
}) => {
  if (isGeneratingAiCustom.value) return;

  await showUsageBasedFeaturesModalOnce("ai_generation", async () => {
    isGeneratingAiCustom.value = true;
    try {
      const contactId = props.selectedBucket?.contact?.id;
      const defaultIcp = props.icps?.find((i) => i.isDefault);

      const response = await AIGenerationService.generateRefine({
        contactId,
        icpId: defaultIcp?.id,
        channel: "sms",
        currentDraft: messageBody.value,
        customInstructions: payload.customInstructions,
        files: payload.attachments,
      });

      messageBody.value = response.body.replace(/<br\s*\/?>/gi, "\n");
      updateCharCount();
      showAiPanel.value = false;
      $toast?.success("AI message refined");
    } catch (error) {
      $toast?.error("Failed to refine AI message");
    } finally {
      isGeneratingAiCustom.value = false;
    }
  });
};

const handleSend = async () => {
  if (!canSend.value || !selectedConversation.value) return;

  await showUsageBasedFeaturesModalOnce("sms", async () => {
    const body = messageBody.value.trim();
    const contactId = props.selectedBucket?.contact?.id;

    if (!contactId) {
      $toast?.error("Contact ID is missing");
      return;
    }

    // Optimistic update via TanStack Query cache
    const queryKey = ["sms-conversation-messages", selectedConversation.value!.id];
    const previousData = queryClient.getQueryData(queryKey) as any;
    if (previousData) {
      queryClient.setQueryData(queryKey, {
        ...previousData,
        messages: [
          ...(previousData.messages || []),
          {
            id: `temp-${Date.now()}`,
            body,
            direction: "outbound",
            status: "sending",
            createdAt: new Date().toISOString(),
            sentAt: new Date().toISOString(),
            isPending: true,
          },
        ],
      });
    }
    clearDraft(selectedConversation.value!.id);
    messageBody.value = "";
    charCount.value = 0;
    await nextTick();
    scrollToBottom();

    sendSMS(
      {
        to: selectedConversation.value!.contactPhoneNumber,
        body,
        contactId,
        conversationId: selectedConversation.value!.id,
        phoneNumberId: selectedConversation.value!.phoneNumberId,
        contactPhoneId: selectedConversation.value!.contactPhoneId,
      },
      {
        onSuccess: () => {
          refetchMessages();
          refetchConversations();
          emit("message-sent");
        },
        onError: (error: any) => {
          console.error("Failed to send SMS:", error);
          if (previousData) {
            queryClient.setQueryData(queryKey, previousData);
          }
          if (!error?.absorbedButRecoverable) {
            const friendlyMessage = getFriendlyTwilioErrorMessage(error);
            $toast?.error(friendlyMessage);
          }
        },
      }
    );
  });
};

// Watch for contact changes
watch(
  () => props.selectedBucket?.contact?.id,
  (newContactId) => {
    contactIdRef.value = newContactId || null;
    // Reset view when contact changes
    showDetailView.value = false;
    selectedConversationId.value = null;
    selectedConversation.value = null;
  },
  { immediate: true }
);

watch(
  () => props.isActive,
  async (isActive) => {
    if (isActive) {
      // Fetch phone numbers if not loaded
      fetchPhoneNumbers();

      if (contactIdRef.value) {
        await refetchConversations();
        if (selectedConversationId.value) {
          await refetchMessages();
          await nextTick();
          scrollToBottom();
        }
      }
    }
  },
  { immediate: true }
);

// Watch messages to scroll to bottom (only when near bottom or initial)
watch(
  () => messages.value?.length,
  (newLen, oldLen) => {
    if (!showDetailView.value) return;
    if (!oldLen && newLen) {
      scrollToBottom();
      return;
    }
    if (newLen && oldLen && newLen > oldLen && messagesContainer.value) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer.value;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
      if (isNearBottom) {
        scrollToBottom();
      }
    }
  },
  { flush: "post" }
);

// Setup socket listeners for real-time updates
useSMSSocketListeners({
  onSMSReceived: (data) => {
    if (data.contactId === contactIdRef.value) {
      queryClient.invalidateQueries({ queryKey: ["sms-conversations", contactIdRef.value] });
      if (data.conversationId && data.conversationId === selectedConversationId.value) {
        queryClient.invalidateQueries({
          queryKey: ["sms-conversation-messages", selectedConversationId.value],
        });
        nextTick(() => scrollToBottom());
      }
      emit("refresh-conversations");
    }
  },
  onSMSSent: (data) => {
    if (data.contactId === contactIdRef.value) {
      queryClient.invalidateQueries({ queryKey: ["sms-conversations", contactIdRef.value] });
      if (data.conversationId && data.conversationId === selectedConversationId.value) {
        queryClient.invalidateQueries({
          queryKey: ["sms-conversation-messages", selectedConversationId.value],
        });
        nextTick(() => scrollToBottom());
      }
      emit("refresh-conversations");
    }
  },
  onSMSStatus: (data) => {
    if (data.contactId === contactIdRef.value) {
      queryClient.invalidateQueries({ queryKey: ["sms-conversations", contactIdRef.value] });
      if (data.conversationId && data.conversationId === selectedConversationId.value) {
        queryClient.invalidateQueries({
          queryKey: ["sms-conversation-messages", selectedConversationId.value],
        });
      }
      emit("refresh-conversations");
    }
  },
});

// Link popover methods
const toggleLinkPopover = () => {
  if (showLinkPopover.value) {
    closeLinkPopover();
  } else {
    closeAllPopovers();

    if (textareaRef.value) {
      const selectionStart = textareaRef.value.selectionStart;
      const selectionEnd = textareaRef.value.selectionEnd;
      if (selectionStart !== selectionEnd) {
        linkText.value = messageBody.value.substring(selectionStart, selectionEnd);
      } else {
        linkText.value = "";
      }
    }

    showLinkPopover.value = true;
    nextTick(() => {
      if (linkPopoverWrapperRef.value) {
        const wrapperRect = linkPopoverWrapperRef.value.getBoundingClientRect();
        const popoverWidth = 240;
        const spacing = 8;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        let right: string | undefined = undefined;
        let left: string | undefined = undefined;
        const rightPosition = viewportWidth - wrapperRect.right;

        if (wrapperRect.right - popoverWidth < 16) {
          left = "16px";
          right = undefined;
        } else {
          right = `${rightPosition}px`;
          left = undefined;
        }

        const bottom = `${viewportHeight - wrapperRect.top + spacing}px`;

        popoverStyle.value = {
          bottom,
          right,
          left,
        };
      }

      if (linkInputRef.value) {
        linkInputRef.value.focus();
      }
    });
  }
};

const closeLinkPopover = () => {
  showLinkPopover.value = false;
  linkUrl.value = "";
  linkText.value = "";
};

const insertLink = () => {
  if (!textareaRef.value) return;

  if (!linkUrl.value || linkUrl.value.trim() === "") {
    return;
  }

  try {
    const selectionStart = textareaRef.value.selectionStart;
    const selectionEnd = textareaRef.value.selectionEnd;

    let url = linkUrl.value.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }

    const text = linkText.value.trim() || url;
    const linkString = text ? `${text} (${url})` : url;

    const beforeText = messageBody.value.substring(0, selectionStart);
    const afterText = messageBody.value.substring(selectionEnd);
    messageBody.value = beforeText + linkString + afterText;

    updateCharCount();

    nextTick(() => {
      if (textareaRef.value) {
        const newCursorPos = selectionStart + linkString.length;
        textareaRef.value.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.value.focus();
      }
    });

    closeLinkPopover();
  } catch (error) {
    console.error("Error inserting link:", error);
    closeLinkPopover();
  }
};

const toggleEmojiPopover = () => {
  if (showEmojiPopover.value) {
    showEmojiPopover.value = false;
  } else {
    closeAllPopovers();
    showEmojiPopover.value = true;
  }
};

const closeEmojiPopover = () => {
  showEmojiPopover.value = false;
};

const handleEmojiSelect = (emoji: any) => {
  if (!textareaRef.value) {
    closeEmojiPopover();
    return;
  }

  try {
    const selectionStart = textareaRef.value.selectionStart;
    const selectionEnd = textareaRef.value.selectionEnd;

    const emojiChar = emoji.emoji || emoji.native || emoji;

    const beforeText = messageBody.value.substring(0, selectionStart);
    const afterText = messageBody.value.substring(selectionEnd);
    messageBody.value = beforeText + emojiChar + afterText;

    updateCharCount();

    nextTick(() => {
      if (textareaRef.value) {
        const newCursorPos = selectionStart + emojiChar.length;
        textareaRef.value.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.value.focus();
      }
    });

    closeEmojiPopover();
  } catch (error) {
    console.error("Error inserting emoji:", error);
    closeEmojiPopover();
  }
};

const closeAllPopovers = () => {
  showLinkPopover.value = false;
  showEmojiPopover.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;

  if (
    showEmojiPopover.value &&
    !target.closest(".sms-composer__emoji-container") &&
    !target.closest("[title='Insert emoji']")
  ) {
    closeEmojiPopover();
  }

  if (showLinkPopover.value && linkPopoverRef.value && !linkPopoverRef.value.contains(target)) {
    closeLinkPopover();
  }
};

onMounted(() => {
  if (props.selectedBucket?.contact?.id) {
    contactIdRef.value = props.selectedBucket.contact.id;
  }

  // Fetch phone numbers if not loaded
  fetchPhoneNumbers();

  if (typeof window !== "undefined") {
    document.addEventListener("click", handleClickOutside);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== "undefined") {
    document.removeEventListener("click", handleClickOutside);
  }
  if (selectedConversationId.value) {
    flushSave(selectedConversationId.value, messageBody.value);
  }
});

watch(
  () => props.pendingCueCardContent,
  (payload) => {
    if (payload && payload.channel === "sms" && payload.content) {
      if (showDetailView.value && selectedConversation.value) {
        applySMSContent(payload.content);
      } else {
        initialModalMessage.value = payload.content;
        showStartChatModal.value = true;
      }
      emit("content-applied");
    }
  }
);
</script>

<style scoped>
.call-panel-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
}

.sms-layout {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sms-list-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sms-sidebar-header {
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
  margin-bottom: 6px;
}

.sms-sidebar-header .ui-button {
  width: 100%;
  background-color: #3e3efe;
  border-color: #3e3efe;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.sms-sidebar-header .ui-button:hover:not(.ui-button--disabled) {
  background-color: #2d2dff;
  border-color: #2d2dff;
}

.sms-sidebar-header .ui-button .ui-button__icon .iconify {
  font-size: 18px !important;
}

.sms-conversations-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  min-height: 200px;
  width: 100%;
}

.sms-content-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sms-list-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sms-main-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sms-main-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.sms-main-header__back-btn {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 2px;
  border-radius: 4px;
  font-size: 1.5rem;
}

.sms-main-header__back-btn:hover {
  color: #111827;
  background: #f3f4f6;
}

.sms-main-header__info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.sms-main-header__name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #111827;
  letter-spacing: 0.01em;
}

.sms-main-header__separator {
  font-size: 0.75rem;
  color: #d1d5db;
  font-weight: 400;
}

.sms-main-header__phone-wrapper {
  display: flex;
  align-items: center;
  gap: 3px;
}

.sms-main-header__phone-icon {
  width: 12px;
  height: 12px;
  color: #9ca3af;
  flex-shrink: 0;
}

.sms-main-header__phone {
  font-size: 0.6875rem;
  font-weight: 400;
  color: #6b7280;
  letter-spacing: 0.01em;
}

.back-to-list-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: -12px;
}

.back-to-list-btn:hover {
  color: #111827;
}

.back-to-list-btn svg {
  font-size: 20px;
}

.sms-messages-container {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
  background-color: #f9fafb;
  display: flex;
  flex-direction: column;
}

.sms-messages-container::-webkit-scrollbar {
  width: 5px;
}

.sms-messages-container::-webkit-scrollbar-track {
  background: transparent;
}

.sms-messages-container::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.sms-messages-container::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.sms-state-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 200px;
  max-height: 300px;
  text-align: center;
}

.sms-state-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
  opacity: 0.6;
}

.sms-loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  min-height: 200px;
  width: 100%;
  color: #6b7280;
  flex: 1;
}

.sms-error-state {
  color: #ef4444;
}

.sms-error-state .sms-state-icon {
  color: #ef4444;
  opacity: 0.8;
}

.sms-empty-state {
  color: #6b7280;
}

.sms-empty-state .sms-state-icon {
  color: #9ca3af;
}

.sms-state-text {
  font-size: 0.875rem;
  margin: 0;
  color: inherit;
}

.retry-btn {
  margin-top: 12px;
  padding: 8px 16px;
  background: #3e3efe;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-btn:hover {
  background: #3230d6;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-bubble {
  display: flex;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-bubble.sent {
  justify-content: flex-end;
}

.message-bubble.received {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 10px 14px;
  border-radius: 12px;
  word-wrap: break-word;
}

.message-bubble.sent .message-content {
  background: #3e3efe;
  color: #ffffff;
  border-bottom-right-radius: 4px;
}

.message-bubble.received .message-content {
  background: #e5e7eb;
  color: #111827;
  border-bottom-left-radius: 4px;
}

.message-content p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 11px;
}

.message-bubble.sent .message-meta {
  justify-content: flex-end;
  color: rgba(255, 255, 255, 0.8);
}

.message-bubble.received .message-meta {
  color: #6b7280;
}

.status-icon {
  width: 14px;
  height: 14px;
}

.status-delivered,
.status-sent {
  color: #10b981;
}

.status-failed,
.status-undelivered {
  color: #ef4444;
}

.status-queued,
.status-accepted,
.status-sending {
  color: #9ca3af;
}

.sms-composer {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  position: relative;
}

.sms-composer__ai-buttons-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.5rem 1rem 0.25rem;
}

.sms-composer__ai-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  height: 24px;
  font-size: 0.7rem;
  min-width: auto;
  white-space: nowrap;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.sms-composer__ai-btn-text {
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1;
}

.sms-composer__ai-btn--reply {
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.1);
}

.sms-composer__ai-btn--reply:hover:not(:disabled) {
  background: rgba(124, 58, 237, 0.2);
  color: #6d28d9;
}

.sms-composer__ai-btn--followup {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
}

.sms-composer__ai-btn--followup:hover:not(:disabled) {
  background: rgba(245, 158, 11, 0.2);
  color: #d97706;
}

.sms-composer__ai-btn--compose {
  color: #0891b2;
  background: rgba(8, 145, 178, 0.1);
}

.sms-composer__ai-btn--compose:hover:not(:disabled) {
  background: rgba(8, 145, 178, 0.2);
  color: #0e7490;
}

.sms-composer__ai-btn--custom {
  color: #4b5563;
  background: rgba(107, 114, 128, 0.12);
}

.sms-composer__ai-btn--custom:hover:not(:disabled) {
  background: rgba(107, 114, 128, 0.2);
  color: #374151;
}

.sms-composer__ai-customization-overlay {
  position: absolute;
  inset: 0;
  z-index: 10001;
}

.sms-composer__ai-customization-container {
  position: absolute;
  bottom: 56px;
  left: 0;
  z-index: 10002;
  pointer-events: auto;
}

.sms-composer__ai-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: auto;
}

.sms-composer__editor-wrapper {
  border-bottom: 1px solid #e5e7eb;
  padding: 0;
}

.sms-composer__textarea {
  width: 100%;
  border: none;
  border-radius: 0;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-family: inherit;
  resize: none;
  transition: none;
  color: #111827;
  line-height: 1.5;
  min-height: 64px;
  background: transparent;
}

.sms-composer__textarea:focus {
  outline: none;
  border: none;
  box-shadow: none;
}

.sms-composer__textarea::placeholder {
  color: #9ca3af;
  font-style: normal;
}

.sms-composer__footer {
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.sms-composer__footer-left {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.sms-composer__char-counter {
  font-size: 0.75rem;
  color: #6b7280;
}

.sms-composer__footer-right {
  display: flex;
  align-items: center;
  position: relative;
}

.sms-composer__footer-inline-tools {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-right: 0.4rem;
}

.sms-composer__footer-icon-btn {
  padding: 0.4rem;
  border-radius: 999px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  transition: all 0.2s;
}

.sms-composer__footer-icon-btn:hover:not(:disabled) {
  background: #e5e7eb;
  color: #374151;
}

.sms-composer__send-btn {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4375rem 0.875rem;
  background: #3e3efe;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sms-composer__send-btn:hover:not(:disabled) {
  background: #3230d6;
}

.sms-composer__send-btn:disabled,
.sms-composer__send-btn--sending {
  opacity: 0.5;
  cursor: not-allowed;
}

.sms-composer__send-btn:disabled {
  pointer-events: auto;
}

.sms-composer__send-btn svg {
  width: 14px;
  height: 14px;
}

.sms-composer__spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.sms-composer__emoji-container {
  position: absolute;
  bottom: 56px;
  left: 16px;
  z-index: 30;
}

.sms-composer__popover-wrapper {
  position: relative;
  z-index: 5;
  overflow: visible;
}

.sms-composer__popover {
  position: fixed;
  z-index: 10000;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: calc(100vw - 32px);
  min-width: 240px;
}

.sms-composer__popover--link {
  width: 240px;
  max-width: 240px;
}

.sms-composer__popover-content {
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sms-composer__link-field-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
}

.sms-composer__link-field-icon {
  font-size: 16px;
  color: #6b7280;
  flex-shrink: 0;
}

.sms-composer__link-input {
  flex: 1;
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 2px !important;
  font-size: 12px;
  outline: none;
  line-height: 1.3;
  transition: border-color 0.2s;
  min-width: 0;
  box-sizing: border-box;
  min-height: 0;
  max-height: 28px;
}

.sms-composer__link-input--text:focus {
  border-color: #3e3efe;
}

.sms-composer__link-input--url {
  padding-right: 50px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sms-composer__link-input--url:focus {
  border-color: #3e3efe;
}

.sms-composer__link-apply-btn {
  position: absolute;
  right: 2px;
  top: 2px;
  bottom: 2px;
  padding: 0 8px;
  border: none;
  border-radius: 2px !important;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  background: #3e3efe;
  color: #ffffff;
  display: flex;
  align-items: center;
  transition: background 0.2s;
  min-height: 0;
  max-height: 24px;
}

.sms-composer__link-apply-btn:hover:not(:disabled) {
  background: #2e2ed8;
}

.sms-composer__link-apply-btn:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

.sms-empty-state--no-phone {
  margin: 0;
  padding: 80px 40px;
  flex: 1;
  min-height: 0;
}

.sms-empty-state--no-conversations {
  margin: 0;
  padding: 32px 30px;
  flex: 1;
  min-height: 0;
}

.sms-empty-state__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 400px;
  gap: 8px;
}

.sms-empty-state__icon {
  font-size: 2.75rem;
  color: #9ca3af;
  margin-bottom: 0;
}

.sms-empty-state__icon--sm {
  font-size: 2.5rem;
}

.sms-empty-state__title {
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
  line-height: 1.4;
}

.sms-empty-state__message {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.sms-list-view :deep(.conversation-list) {
  width: 100%;
  min-width: 100%;
  border-right: none;
}

/* ICP Picker positioning */
.sms-composer__icp-picker-positioned {
  position: absolute;
  bottom: 100%;
  left: 16px;
  right: 16px;
  margin-bottom: 6px;
  z-index: 10;
}

.sms-composer__ai-spinner {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  border-top-color: #f97316;
  animation: sms-ai-spin 0.7s linear infinite;
  display: inline-block;
  flex-shrink: 0;
}

@keyframes sms-ai-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
